<template>
  <div>
    <!-- Header -->
    <div
      class="row items-center q-pa-sm"
      :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-3'"
    >
      <div class="text-subtitle1 text-weight-medium">Bulletins</div>
      <q-space />
      <q-btn
        unelevated
        dense
        icon="filter_list"
        padding="xs md"
        color="secondary"
      >
        <q-menu :offset="[0, 8]">
          <q-list dense style="min-width: 160px">
            <q-item tag="label">
              <q-item-section side>
                <q-checkbox
                  :value="allSelected"
                  @input="toggleAll"
                  color="secondary"
                  dense
                />
              </q-item-section>
              <q-item-section>All</q-item-section>
            </q-item>
            <q-separator />
            <q-item
              v-for="cat in accessibleCategories"
              :key="cat.id"
              tag="label"
            >
              <q-item-section side>
                <q-checkbox
                  :value="selectedCategories.includes(cat.id)"
                  @input="toggleCategory(cat.id)"
                  color="secondary"
                  dense
                />
              </q-item-section>
              <q-item-section>
                {{ cat.item }}
              </q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </q-btn>
      <q-btn
        unelevated
        dense
        icon="add"
        color="primary"
        padding="xs md"
        class="q-ml-sm"
        @click="addBulletin()"
      />
    </div>

    <!-- Category sections -->
    <div
      class="q-pa-md scroll"
      :style="`height: ${store.state.pageHeight - 142}px`"
    >
      <div
        v-for="cat in visibleCategories"
        :key="cat.id"
        class="q-mb-md bulletin-col"
      >
        <div
          class="row items-center q-px-sm q-py-sm q-mb-xs rounded-borders"
          :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-3'"
          :style="`border-left: 4px solid ${categoryHex(cat)}`"
        >
          <div class="text-subtitle2 text-weight-bold">
            {{ cat.item }}
          </div>
        </div>
        <div v-for="b in categoryItems(cat.id)" :key="b.id" class="q-mt-sm">
          <div class="row no-wrap">
            <div
              :style="`width: 4px; min-width: 4px; background: ${categoryHex(
                cat
              )}; border-radius: 4px 0 0 4px`"
            />
            <div
              style="
                border: 1px solid rgba(0, 0, 0, 0.12);
                border-left: none;
                border-radius: 0 4px 4px 0;
                background: white;
                flex: 1;
                min-width: 0;
              "
            >
              <div class="row no-wrap">
                <div class="col column justify-between q-pa-sm">
                  <div>
                    <div class="row items-center q-mb-xs">
                      <div
                        v-if="b.attachments && b.attachments.length > 0"
                        class="q-ml-xs text-primary row inline items-center no-wrap"
                        style="
                          background: #eee;
                          padding: 1.5px 4px;
                          font-size: 11px;
                          line-height: 1.3;
                          border-radius: 3px;
                        "
                      >
                        <q-icon
                          name="photo_library"
                          size="12px"
                          class="q-mr-xs"
                        />
                        {{ b.attachments.length }}
                      </div>
                      <span class="text-caption text-grey q-ml-sm">
                        {{ formatDate(b.date) }}
                      </span>
                    </div>
                    <div v-if="b.title" class="text-body2 text-weight-bold">
                      {{ b.title }}
                    </div>
                    <div
                      v-if="b.body"
                      class="text-body2 bulletin-body"
                      v-html="b.body"
                      v-sortable-list="(h) => onListReorder(b, h)"
                      @click="handleBodyClick($event, b)"
                    />
                  </div>
                  <div class="q-mt-sm">
                    <q-btn
                      unelevated
                      dense
                      no-caps
                      color="grey-2"
                      text-color="primary"
                      padding="1px 4px"
                      style="min-height: 0; font-size: 11px; line-height: 1.3"
                    >
                      {{ bulletinAccessLabel(b) }}
                    </q-btn>
                  </div>
                </div>
                <div class="column justify-end q-pa-sm" style="gap: 4px">
                  <q-btn
                    unelevated
                    icon="photo_library"
                    size="sm"
                    color="grey-2"
                    :text-color="
                      b.attachments && b.attachments.length
                        ? 'primary'
                        : 'grey-6'
                    "
                    @click="openAttachments(b)"
                  />
                  <q-btn
                    unelevated
                    icon="edit"
                    size="sm"
                    color="grey-2"
                    text-color="primary"
                    @click="editBulletin(b)"
                  />
                  <q-btn
                    unelevated
                    icon="delete"
                    size="sm"
                    color="grey-2"
                    text-color="negative"
                    @click="confirmDelete(b)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / Edit dialog -->
    <q-dialog persistent v-model="showDialog" position="top">
      <q-card style="width: 500px; max-width: 95vw">
        <q-card-section>
          <div class="text-h6 text-center">
            {{ editItem.id === 'new' ? 'New' : 'Edit' }}
            Bulletin
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <q-form @submit="onSubmit" class="q-gutter-sm">
            <div>
              <q-btn
                unelevated
                dense
                no-caps
                size="sm"
                color="secondary"
                :label="categoryName(editItem.category_id)"
                padding="xs sm"
              >
                <q-icon name="arrow_drop_down" size="16px" right />
                <q-menu :offset="[0, 4]">
                  <q-list dense separator style="min-width: 160px">
                    <q-item
                      v-for="cat in accessibleCategories"
                      :key="cat.id"
                      clickable
                      v-close-popup
                      :active="editItem.category_id === cat.id"
                      active-class="text-teal"
                      @click="editItem.category_id = cat.id"
                    >
                      <q-item-section>
                        {{ cat.item }}
                      </q-item-section>
                      <q-item-section
                        side
                        v-if="editItem.category_id === cat.id"
                      >
                        <q-icon name="check" color="secondary" size="xs" />
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </div>
            <q-input
              v-model="editItem.title"
              label="Title (optional)"
              outlined
            />
            <q-editor
              v-model="editItem.body"
              min-height="5rem"
              :toolbar="[
                ['bold', 'italic', 'underline'],
                ['unordered', 'ordered'],
                [
                  {
                    icon: 'format_align_left',
                    fixedLabel: true,
                    options: ['left', 'center', 'right', 'justify']
                  }
                ],
                ['undo', 'redo']
              ]"
            />
            <div
              class="q-pa-sm rounded-borders"
              style="border: 1px solid #e0e0e0"
            >
              <q-checkbox
                v-model="editItem.display_default"
                label="Display on Main Board"
                :true-value="1"
                :false-value="0"
                dense
              />
            </div>
            <div
              class="q-mt-sm q-pa-sm rounded-borders"
              style="border: 1px solid #e0e0e0"
            >
              <q-checkbox
                v-model="editItem.all_users"
                label="All Users"
                :true-value="1"
                :false-value="0"
                dense
                @input="onAllUsersToggle"
              />
              <div v-if="!editItem.all_users" class="q-mt-xs q-ml-sm">
                <div class="text-caption text-grey q-mb-xs">Visible to:</div>
                <div
                  class="column q-gutter-xs"
                  style="max-height: 200px; overflow-y: auto"
                >
                  <q-checkbox
                    v-for="u in userOptions"
                    :key="u.value"
                    :value="editItem.user_ids.includes(u.value)"
                    @input="toggleUser(u.value)"
                    :label="u.label"
                    :disable="u.value === $store.state.user.user_id"
                    dense
                    size="sm"
                  />
                </div>
              </div>
            </div>
            <div class="row justify-end q-gutter-sm q-mt-sm">
              <q-btn
                label="Cancel"
                color="primary"
                flat
                @click="showDialog = false"
              />
              <q-btn
                label="Save"
                color="primary"
                type="submit"
                :disable="
                  !editItem.all_users &&
                  (!editItem.user_ids || editItem.user_ids.length === 0)
                "
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Delete Confirmation dialog -->
    <q-dialog persistent v-model="showDeleteDialog">
      <q-card style="width: 400px; max-width: 95vw">
        <q-card-section>
          <div
            class="text-subtitle1 text-center ellipsis"
            :class="{ 'text-weight-bold': deleteItem.hasTitle }"
          >
            {{ deleteItem.title }}
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <div class="text-center">
            {{ deleteItem.message }}
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section class="row justify-end q-gutter-sm">
          <q-btn
            flat
            label="Cancel"
            color="primary"
            @click="showDeleteDialog = false"
          />
          <q-btn
            label="Delete"
            icon="delete"
            color="negative"
            @click="onDeleteConfirm"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Attachments dialog -->
    <q-dialog v-model="showAttachmentDialog" maximized>
      <q-card>
        <div class="row items-center q-pa-sm">
          <q-btn
            outline
            label="Back"
            color="primary"
            icon="arrow_back"
            padding="xs sm"
            @click="showAttachmentDialog = false"
          />
          <q-space />
          <div class="text-h6">
            {{ attachmentBulletin.title }}
          </div>
        </div>
        <q-separator />
        <q-card-section>
          <Attachments
            v-if="showAttachmentDialog"
            :parentObj="attachmentBulletin"
            :parentType="'bulletin'"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- List Item Action dialog -->
    <q-dialog persistent v-model="showListItemDialog">
      <q-card style="width: 400px; max-width: 95vw">
        <q-card-section>
          <div class="text-subtitle1 text-center ellipsis">
            {{ listItemAction.text }}
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section class="row no-wrap justify-end q-gutter-sm">
          <q-btn
            flat
            dense
            label="Cancel"
            color="primary"
            padding="xs sm"
            @click="showListItemDialog = false"
          />
          <q-btn
            unelevated
            dense
            label="Move"
            icon="drive_file_move"
            color="primary"
            padding="xs sm"
            @click="openMoveDialog"
          />
          <q-btn
            unelevated
            dense
            label="Delete"
            icon="delete"
            color="negative"
            padding="xs sm"
            @click="onListItemDelete"
          />
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Move List Item dialog -->
    <q-dialog persistent v-model="showMoveDialog">
      <q-card style="width: 400px; max-width: 95vw">
        <q-card-section>
          <div class="text-h6 text-center">Move to...</div>
        </q-card-section>
        <q-separator />
        <q-card-section class="scroll" style="max-height: 60vh">
          <template v-for="cat in accessibleCategories">
            <div
              v-if="moveTargets(cat.id).length > 0"
              :key="'mv-' + cat.id"
              class="q-mb-sm"
            >
              <div
                class="text-caption text-weight-bold q-mb-xs"
                :style="'color: ' + categoryHex(cat)"
              >
                {{ cat.item }}
              </div>
              <q-list dense separator>
                <q-item
                  v-for="b in moveTargets(cat.id)"
                  :key="'mv-b-' + b.id"
                  clickable
                  v-close-popup
                  @click="moveListItem(b)"
                >
                  <q-item-section>
                    <q-item-label class="ellipsis">
                      {{ b.title || stripHtml(b.body) }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
          </template>
        </q-card-section>
        <q-separator />
        <q-card-section class="row justify-end">
          <q-btn
            flat
            label="Cancel"
            color="primary"
            @click="showMoveDialog = false"
          />
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import store from '@/store';
import { parseTimeStamp } from '@/lib/date-utils';
import { colors } from 'quasar';
import Attachments from '@/components/Attachments.vue';
import Sortable from 'sortablejs';

function initListSort(el, cb) {
  if (el._sortables) {
    el._sortables.forEach((s) => s.destroy());
  }
  el._sortables = [];
  el._dragging = false;
  el.querySelectorAll('.li-drag-handle').forEach((h) => h.remove());
  el.querySelectorAll('.li-text').forEach((s) =>
    s.replaceWith(...s.childNodes)
  );
  el.querySelectorAll('li').forEach((li) => {
    const h = document.createElement('i');
    h.className = 'li-drag-handle material-icons';
    h.textContent = 'drag_indicator';
    const span = document.createElement('span');
    span.className = 'li-text';
    while (li.firstChild) span.appendChild(li.firstChild);
    li.appendChild(span);
    li.prepend(h);
  });
  el.querySelectorAll('ul, ol').forEach((list) => {
    el._sortables.push(
      Sortable.create(list, {
        animation: 150,
        handle: '.li-drag-handle',
        onStart() {
          el._dragging = true;
        },
        onEnd() {
          el._dragging = false;
          const clone = el.cloneNode(true);
          clone.querySelectorAll('.li-drag-handle').forEach((x) => x.remove());
          clone
            .querySelectorAll('.li-text')
            .forEach((s) => s.replaceWith(...s.childNodes));
          cb(clone.innerHTML);
        }
      })
    );
  });
}

export default {
  name: 'FarmBulletins',
  components: { Attachments },
  directives: {
    sortableList: {
      inserted(el, binding) {
        initListSort(el, binding.value);
      },
      componentUpdated(el, binding) {
        if (el._dragging) return;
        initListSort(el, binding.value);
      },
      unbind(el) {
        if (el._sortables) {
          el._sortables.forEach((s) => s.destroy());
        }
      }
    }
  },
  props: {
    farmId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      store,
      selectedCategories: [],
      filtersInitialized: false,
      showDialog: false,
      showDeleteDialog: false,
      deleteItem: {
        title: '',
        message: '',
        callback: null
      },
      showAttachmentDialog: false,
      editItem: this.newBulletin(),
      attachmentBulletin: { attachments: [] },
      showListItemDialog: false,
      listItemAction: {
        bulletin: null,
        idx: -1,
        text: ''
      },
      showMoveDialog: false
    };
  },
  computed: {
    farmBulletins() {
      const fid = parseInt(this.farmId);
      return this.$store.state.bulletins.filter((b) => b.farm_id === fid);
    },
    categories() {
      return this.$store.state.bulletinCategories;
    },
    accessibleCategories() {
      if (this.$store.getters.isSuperAdmin) {
        return this.categories;
      }
      return this.categories.filter((c) => !c.admin_only);
    },
    allSelected() {
      return (
        this.accessibleCategories.length > 0 &&
        this.accessibleCategories.every((c) =>
          this.selectedCategories.includes(c.id)
        )
      );
    },
    visibleCategories() {
      return this.accessibleCategories
        .filter((c) => this.selectedCategories.includes(c.id))
        .filter((c) => this.categoryItems(c.id).length > 0);
    },
    userOptions() {
      return this.$store.state.userList
        .map((u) => ({
          label: u.display_name,
          value: u.user_id
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  },
  watch: {
    accessibleCategories: {
      immediate: true,
      handler(val) {
        if (!this.filtersInitialized && val.length > 0) {
          this.filtersInitialized = true;
          this.selectedCategories = val.map((c) => c.id);
        }
      }
    }
  },
  methods: {
    toggleAll() {
      if (this.allSelected) {
        this.selectedCategories = [];
      } else {
        this.selectedCategories = this.accessibleCategories.map((c) => c.id);
      }
    },
    toggleCategory(catId) {
      const idx = this.selectedCategories.indexOf(catId);
      if (idx >= 0) {
        this.selectedCategories.splice(idx, 1);
      } else {
        this.selectedCategories.push(catId);
      }
    },
    categoryItems(catId) {
      return this.farmBulletins
        .filter((b) => b.category_id === catId)
        .sort((a, b) => a.sort_order - b.sort_order);
    },
    categoryName(catId) {
      const cat = this.categories.find((c) => c.id === catId);
      return cat ? cat.item : '';
    },
    categoryHex(cat) {
      const name = cat.color || 'secondary';
      return colors.getPaletteColor(name);
    },
    bulletinAccessLabel(b) {
      if (b.all_users || b.all_users == null) {
        return 'All Users';
      }
      if (!b.user_ids || !b.user_ids.length) {
        return 'All Users';
      }
      const list = this.$store.state.userList;
      return b.user_ids
        .map((id) => {
          const u = list.find((x) => x.user_id == id);
          return u ? u.display_name : '';
        })
        .filter(Boolean)
        .sort((a, c) => a.localeCompare(c))
        .join(', ');
    },
    onAllUsersToggle(val) {
      if (!val) {
        const uid = this.$store.state.user.user_id;
        if (!this.editItem.user_ids.includes(uid)) {
          this.editItem.user_ids.push(uid);
        }
      }
    },
    toggleUser(userId) {
      const uid = this.$store.state.user.user_id;
      if (userId === uid) return;
      const idx = this.editItem.user_ids.indexOf(userId);
      if (idx >= 0) {
        this.editItem.user_ids.splice(idx, 1);
      } else {
        this.editItem.user_ids.push(userId);
      }
    },
    formatDate(ts) {
      return parseTimeStamp(ts, 'short');
    },
    newBulletin() {
      const defaultCatId =
        this.categories && this.categories.length > 0
          ? this.categories[0].id
          : 0;
      return {
        id: 'new',
        user_id: this.$store ? this.$store.state.user.user_id : 0,
        farm_id: parseInt(this.farmId),
        title: '',
        body: '',
        category_id: defaultCatId,
        sort_order: 0,
        display_default: 1,
        date: Math.floor(Date.now() / 1000),
        all_users: 0,
        user_ids: [this.$store ? this.$store.state.user.user_id : 0]
      };
    },
    addBulletin(categoryId) {
      this.editItem = this.newBulletin();
      if (categoryId) {
        this.editItem.category_id = categoryId;
      }
      this.showDialog = true;
    },
    changeCategory(bulletin, catId) {
      if (bulletin.category_id === catId) return;
      bulletin.category_id = catId;
      store.dispatch('updateBulletin', { bulletin });
    },
    editBulletin(bulletin) {
      this.editItem = JSON.parse(JSON.stringify(bulletin));
      if (!this.editItem.user_ids) {
        this.editItem.user_ids = [];
      }
      if (this.editItem.display_default == null) {
        this.editItem.display_default = 1;
      }
      if (this.editItem.all_users == null) {
        this.editItem.all_users = 1;
      }
      this.showDialog = true;
    },
    onSubmit() {
      const dispatch =
        this.editItem.id === 'new' ? 'createBulletin' : 'updateBulletin';

      if (dispatch === 'createBulletin') {
        this.editItem.date = Math.floor(Date.now() / 1000);
      }

      store
        .dispatch(dispatch, {
          bulletin: this.editItem
        })
        .then((results) => {
          if (results.sent === false || results.reasonCode !== 0) {
            this.$finishResult.handleResultsAsync(results).then((response) => {
              if (response === 'retry') {
                this.onSubmit();
              }
            });
          } else {
            this.$q.notify({
              message: 'Bulletin Saved',
              color: 'primary'
            });
          }
        });

      this.showDialog = false;
    },
    stripHtml(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      div.querySelectorAll('li').forEach((li) => li.prepend(' \u00B7 '));
      return (div.textContent || '').replace(/^\s*\u00B7\s*/, '').trim();
    },
    handleBodyClick(event, bulletin) {
      if (event.target.closest('.li-drag-handle')) return;
      const li = event.target.closest('li');
      if (!li) return;
      event.preventDefault();
      const container = event.currentTarget;
      const allLis = container.querySelectorAll('li');
      const idx = Array.from(allLis).indexOf(li);
      if (idx < 0) return;
      const clone = li.cloneNode(true);
      clone.querySelectorAll('.li-drag-handle').forEach((h) => h.remove());
      clone
        .querySelectorAll('.li-text')
        .forEach((s) => s.replaceWith(...s.childNodes));
      const raw = clone.textContent.trim();
      const text = raw.substring(0, 50) + (raw.length > 50 ? '...' : '');
      this.listItemAction = {
        bulletin,
        idx,
        text
      };
      this.showListItemDialog = true;
    },
    onListReorder(bulletin, html) {
      bulletin.body = html;
      store.dispatch('updateBulletin', {
        bulletin
      });
    },
    removeListItem(bulletin, idx) {
      const div = document.createElement('div');
      div.innerHTML = bulletin.body;
      const items = div.querySelectorAll('li');
      if (!items[idx]) return;
      const parent = items[idx].parentElement;
      items[idx].remove();
      if (parent && parent.children.length === 0) {
        parent.remove();
      }
      bulletin.body = div.innerHTML;
      store.dispatch('updateBulletin', {
        bulletin
      });
    },
    onListItemDelete() {
      this.showListItemDialog = false;
      this.removeListItem(
        this.listItemAction.bulletin,
        this.listItemAction.idx
      );
    },
    openMoveDialog() {
      this.showListItemDialog = false;
      this.showMoveDialog = true;
    },
    moveTargets(catId) {
      const src = this.listItemAction.bulletin;
      return this.categoryItems(catId).filter((b) => !src || b.id !== src.id);
    },
    moveListItem(target) {
      const src = this.listItemAction.bulletin;
      const idx = this.listItemAction.idx;
      const srcDiv = document.createElement('div');
      srcDiv.innerHTML = src.body;
      const srcItems = srcDiv.querySelectorAll('li');
      if (!srcItems[idx]) return;
      const liEl = srcItems[idx];
      const srcParent = liEl.parentElement;
      liEl.remove();
      if (srcParent && srcParent.children.length === 0) {
        srcParent.remove();
      }
      src.body = srcDiv.innerHTML;
      const tgtDiv = document.createElement('div');
      tgtDiv.innerHTML = target.body;
      let list = tgtDiv.querySelector('ul, ol');
      if (!list) {
        list = document.createElement('ul');
        tgtDiv.appendChild(list);
      }
      list.appendChild(liEl);
      target.body = tgtDiv.innerHTML;
      store.dispatch('updateBulletin', {
        bulletin: src
      });
      store.dispatch('updateBulletin', {
        bulletin: target
      });
      this.showMoveDialog = false;
      this.$q.notify({
        message: 'Item moved',
        color: 'primary'
      });
    },
    openAttachments(bulletin) {
      this.attachmentBulletin = JSON.parse(JSON.stringify(bulletin));
      this.showAttachmentDialog = true;
    },
    confirmDelete(bulletin) {
      this.deleteItem = {
        hasTitle: !!bulletin.title,
        title: bulletin.title || this.stripHtml(bulletin.body),
        message: 'Delete this bulletin?',
        callback: () => {
          this.dispatchDelete(bulletin);
        }
      };
      this.showDeleteDialog = true;
    },
    onDeleteConfirm() {
      if (this.deleteItem.callback) {
        this.deleteItem.callback();
      }
      this.showDeleteDialog = false;
    },
    dispatchDelete(bulletin) {
      store.dispatch('deleteBulletin', { bulletin }).then((results) => {
        this.$finishResult.handleResultsAsync(results).then((response) => {
          if (response === 'retry') {
            this.dispatchDelete(bulletin);
          }
        });
      });
    }
  }
};
</script>

<style scoped>
.bulletin-col {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}
.bulletin-body >>> ul,
.bulletin-body >>> ol {
  margin: 0;
  padding-left: 40px;
}
.bulletin-body >>> li {
  position: relative;
}
.bulletin-body >>> p {
  margin: 0;
}
@media (hover: hover) {
  .bulletin-body >>> .li-text {
    cursor: pointer;
  }
  .bulletin-body >>> .li-text:hover {
    text-decoration: line-through;
  }
}
.bulletin-body >>> .li-drag-handle {
  position: absolute;
  left: -36px;
  top: 1px;
  cursor: grab;
  color: #bbb;
  font-size: 16px;
  user-select: none;
}
</style>
